// HEADER-DOCS-011: SmsDisabledBanner — chat composer warning
// Spec: §4.5, §17.1, §19.I
//
// Presentational banner mounted inside SimpleChatBox, above the message input.
// It does NOT fetch anything: SimpleChatBox already loads every input it needs
// (recipient notification settings, phone/verification, plan gate), and passing
// them as props keeps the banner in lock-step with the real SMS send condition
// in SimpleChatBox.handleSend:
//   phoneNumber && isVerifiedPhoneNumber && conversationSid && smsEnabled && !planGate
//
// Two variants:
//   (B) sender-plan   — SMS is blocked by the plan gate (Starter / no plan).
//   (A) recipient-off — recipient has SMS receive off, or has no verified phone.
// Priority (§19.I): (B) wins over (A).
//
// Copy decisions (Michelle, ClickUp 86akt4ngd):
//   - (A) is ONE unified message for "no verified phone" and "SMS turned off".
//   - (B) for a counselor is message-only (no CTA): "SMS not available on this
//     student's plan." Plans belong to students, so parents see the same wording
//     but keep the Upgrade button; students see "your current plan".

import React, { useEffect, useState } from 'react';
import { Alert, Button, Link } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { routers } from 'router/router';

export type SmsOffReason = 'sms-disabled' | 'no-verified-phone';

export interface SmsDisabledBannerProps {
  /** Role of the signed-in user (the SENDER). */
  currentUserRole: 'student' | 'parent' | 'counselor';
  /** Id of the OTHER party in the conversation; scopes the per-recipient dismiss key. */
  recipientUserId: number;
  /** Recipient e-mail, used for the "Ask them to enable SMS" mailto link. */
  recipientEmail?: string;
  /** True when the plan gate blocks SMS (same flag SimpleChatBox uses before sending). */
  planBlocksSms: boolean;
  /**
   * Recipient SMS-receive setting. `null` = not loaded yet → render nothing,
   * so the banner never flashes before the settings request resolves.
   */
  recipientSmsEnabled: boolean | null;
  /** Recipient has a phone number AND it is verified. */
  recipientHasVerifiedPhone: boolean;
}

// Per-session dismiss keys (§4.5). Plan key is global; recipient key is per contact.
const PLAN_DISMISS_KEY = 'sms-plan-warning-dismissed';
const recipientDismissKey = (recipientUserId: number) =>
  `sms-disabled-warning-dismissed-${recipientUserId}`;

const readDismissed = (key: string): boolean => {
  try {
    return sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
};

export const SmsDisabledBanner: React.FC<SmsDisabledBannerProps> = ({
  currentUserRole,
  recipientUserId,
  recipientEmail,
  planBlocksSms,
  recipientSmsEnabled,
  recipientHasVerifiedPhone,
}) => {
  const history = useHistory();

  // §19.I priority: sender-plan gate wins over recipient-off.
  const recipientReason: SmsOffReason | null =
    recipientSmsEnabled === null
      ? null
      : !recipientHasVerifiedPhone
      ? 'no-verified-phone'
      : !recipientSmsEnabled
      ? 'sms-disabled'
      : null;
  const variant: 'sender-plan' | 'recipient-off' | null = planBlocksSms
    ? 'sender-plan'
    : recipientReason
    ? 'recipient-off'
    : null;

  // Dismiss state is keyed by the active variant, so dismissing one variant
  // never hides the other, and switching recipient re-reads its own key.
  const dismissKey =
    variant === 'sender-plan'
      ? PLAN_DISMISS_KEY
      : variant === 'recipient-off'
      ? recipientDismissKey(recipientUserId)
      : null;
  const [dismissed, setDismissed] = useState<boolean>(() =>
    dismissKey ? readDismissed(dismissKey) : false
  );
  useEffect(() => {
    setDismissed(dismissKey ? readDismissed(dismissKey) : false);
  }, [dismissKey]);

  if (!variant || !dismissKey || dismissed) return null;

  const onDismiss = () => {
    try {
      sessionStorage.setItem(dismissKey, '1');
    } catch {
      // Storage unavailable (private mode) — dismiss for this render tree only.
    }
    setDismissed(true);
  };

  if (variant === 'sender-plan') {
    // Students upgrade their own plan; parents pay for the student from the same
    // billing tab. Counselors cannot upgrade a student's plan → no CTA.
    const canUpgrade = currentUserRole === 'student' || currentUserRole === 'parent';
    return (
      <Alert
        severity="info"
        onClose={onDismiss}
        className="sms-disabled-banner"
        sx={{ borderRadius: 1, mx: 1.5, mt: 1, py: 0, fontSize: 13 }}
        action={
          canUpgrade ? (
            <Button
              size="small"
              color="inherit"
              onClick={() => history.push(`${routers.settings.path}?active-tab=5`)}
            >
              Upgrade
            </Button>
          ) : undefined
        }
      >
        <strong>
          {currentUserRole === 'student'
            ? 'SMS not available on your current plan.'
            : "SMS not available on this student's plan."}
        </strong>{' '}
        Your message will still reach them via in-app and email.
      </Alert>
    );
  }

  // variant === 'recipient-off'
  const mailto =
    recipientEmail && recipientReason === 'sms-disabled'
      ? `mailto:${recipientEmail}?subject=${encodeURIComponent(
          'Enable SMS on GradMap'
        )}&body=${encodeURIComponent(
          "Hi, could you enable SMS notifications in your GradMap account settings? I want to reach you via text when there's something time-sensitive. Thanks!"
        )}`
      : null;

  return (
    <Alert
      severity="info"
      onClose={onDismiss}
      className="sms-disabled-banner"
      sx={{ borderRadius: 1, mx: 1.5, mt: 1, py: 0, fontSize: 13 }}
    >
      {/* Same text for both reasons (no verified phone / SMS off) per Michelle. */}
      <strong>SMS is off for this contact.</strong>{' '}
      Your message will still reach them via in-app and email, but not as a text.{' '}
      {mailto && (
        <Link href={mailto} rel="noopener">
          Ask them to enable SMS →
        </Link>
      )}
    </Alert>
  );
};
